// Per-show theming. Backend sanitizer is the source of truth for what keys
// and shapes are valid; the widget injects whatever survives that contract.
// Keeps a 1-line hex / font regex as a guard against localStorage tampering.

import { useEffect } from 'react';

const STYLING_STORAGE_KEY_PREFIX = 'fireknife-show-styling-';
const FONTS_LINK_ID = 'fireknife-show-fonts';
const STATE_KEY = '__fireknifeStylingProps';

const HEX = /^#(?:[0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
const FONT = /^[A-Za-z][A-Za-z0-9 -]{0,63}$/;
// Text/bg tokens get a full lightness flip in dark mode; brand tokens get a
// gentler chroma reduction so they stay recognisable but stop searing eyes.
const DARK_INVERT_KEYS = new Set(['header-color', 'default-color', 'default-bg']);

export type ShowStyling = {
    styling?: Record<string, string>;
    fonts?: { header?: string; body?: string };
    nightMode?: 'auto' | 'light' | 'dark';
};

function fontsHref(fonts: ShowStyling['fonts']): string | null {
    const families = [
        ...new Set(
            [fonts?.header, fonts?.body].filter(
                (f): f is string => !!f && FONT.test(f) && f !== 'Inter',
            ),
        ),
    ];
    if (families.length === 0) return null;
    const params = families
        .map((f) => `family=${encodeURIComponent(f)}:wght@400;500;600;700`)
        .join('&');
    return `https://fonts.googleapis.com/css2?${params}&display=swap`;
}

export function applyShowStyling(root: HTMLElement | null, data: ShowStyling): void {
    if (!root) return;
    const el = root as HTMLElement & { [STATE_KEY]?: Set<string> };
    // Clear inline props we set previously so removed keys don't linger.
    for (const prop of el[STATE_KEY] ?? []) root.style.removeProperty(prop);
    const applied = new Set<string>();

    const dark =
        data.nightMode === 'dark' ||
        (data.nightMode === 'auto' &&
            typeof window !== 'undefined' &&
            window.matchMedia('(prefers-color-scheme: dark)').matches);

    for (const [k, v] of Object.entries(data.styling ?? {})) {
        if (typeof v !== 'string' || !HEX.test(v)) continue;
        const value = !dark
            ? v
            : DARK_INVERT_KEYS.has(k)
              ? `oklch(from ${v} calc(1 - l) c h)`
              : `oklch(from ${v} l calc(c * 0.8) h)`;
        root.style.setProperty(`--fireknife-${k}`, value);
        applied.add(`--fireknife-${k}`);
        // "Page background" admin control must also drive --fireknife-white-bg
        // (the card surface) since most widget surfaces consume that token.
        if (k === 'default-bg') {
            root.style.setProperty('--fireknife-white-bg', value);
            applied.add('--fireknife-white-bg');
        }
        // "Body text color" must also drive --fireknife-black: most widget
        // body copy (container default, form labels, datepicker text,
        // summaries) reads --fireknife-black, not --fireknife-default-color.
        if (k === 'default-color') {
            root.style.setProperty('--fireknife-black', value);
            applied.add('--fireknife-black');
        }
    }
    for (const slot of ['header', 'body'] as const) {
        const name = data.fonts?.[slot];
        if (name && FONT.test(name)) {
            root.style.setProperty(`--fireknife-font-${slot}`, `'${name}', sans-serif`);
            applied.add(`--fireknife-font-${slot}`);
        }
    }
    el[STATE_KEY] = applied;

    if (data.nightMode) {
        root.setAttribute('data-fireknife-theme', data.nightMode);
        root.style.colorScheme = data.nightMode === 'auto' ? 'light dark' : data.nightMode;
    } else {
        root.removeAttribute('data-fireknife-theme');
        root.style.removeProperty('color-scheme');
    }

    const href = fontsHref(data.fonts);
    const link = document.getElementById(FONTS_LINK_ID) as HTMLLinkElement | null;
    if (!href) link?.remove();
    else if (link) {
        if (link.href !== href) link.href = href;
    } else {
        const next = document.createElement('link');
        next.id = FONTS_LINK_ID;
        next.rel = 'stylesheet';
        next.href = href;
        document.head.appendChild(next);
    }
}

export function applyPersistedStyling(root: HTMLElement, showId: number): void {
    try {
        const raw = localStorage.getItem(`${STYLING_STORAGE_KEY_PREFIX}${showId}`);
        if (raw) applyShowStyling(root, JSON.parse(raw) as ShowStyling);
    } catch {
        // localStorage unavailable / corrupt
    }
}

function persistStyling(showId: number, data: ShowStyling): void {
    try {
        localStorage.setItem(`${STYLING_STORAGE_KEY_PREFIX}${showId}`, JSON.stringify(data));
    } catch {
        // localStorage unavailable
    }
}

export function useShowStyling(show: { id?: number; marketing?: unknown } | undefined): void {
    const m = show?.marketing as ShowStyling | undefined;
    useEffect(() => {
        if (!show?.id) return;
        const data: ShowStyling = { styling: m?.styling, fonts: m?.fonts, nightMode: m?.nightMode };
        applyShowStyling(document.getElementById('fireknife-widget-root'), data);
        persistStyling(show.id, data);
    }, [show?.id, m?.styling, m?.fonts, m?.nightMode]);
}
